import React from 'react';

/**
 * /TubePro_PLC_Debugger
 *
 * Storefront for the TubePro PLC Debug Log Injector Windows EXE.
 * Static page; the actual injection work happens in the downloaded app.
 */

const APP_VERSION = '1.1';
const EXE_SIZE_MB = 15;
const EXE_HREF = '/TubePro_PLC_Debugger/TubeProPLCDebugger.exe';

const FEATURES: Array<{ head: string; body: string }> = [
    {
        head: 'Logs the points that matter',
        body: 'A trace at program entry, at the start of every If branch (with hierarchical position), at the program end, and immediately before every Custom PLC, Parallel PLC, and Execute Script call. Quiet on the rest of the program.',
    },
    {
        head: 'Verification report on every run',
        body: 'Per-file diff, original vs. new step counts, logs added, XML re-parse check, and SHA-256 of every non-.stps file before and after. Saved next to the output.',
    },
    {
        head: 'Preserves existing PLC Logs',
        body: 'Never modifies or removes a Log step that was already in the program. Re-running on a debug-annotated backup is a no-op.',
    },
    {
        head: 'Operator-safe',
        body: 'Only .stps files under data/CamSteps3 are touched. Everything else — ports, timing, machine.config, repos, vars — is checksummed before and after to prove it was never read with intent to write.',
    },
];

const RULES: Array<[string, string]> = [
    ['At program entry', 'PLC {N} {name} — first <Step> child of every <Program>.'],
    ['Inside If branches', 'PLC {N} {name}: True {hier} or False {hier} — first child of every IfTrue / IfFalse, with hierarchical position (1.0, 1.1, 1.1.1, …).'],
    ['Before call sites', 'PLC {N} calls P PLC{target} for Custom PLC and Parallel PLC steps; PLC {N} calls JS Script {target} for Execute Script steps. Inserted as the prior sibling.'],
    ['At program end', 'PLC {N} {name} END — appended as the last <Step> of every <Program>.'],
    ['Existing logs', 'Left exactly as-is. Re-running the tool produces a byte-identical output.'],
];

const SCOPE: Array<[string, string]> = [
    ['data/CamSteps3/**/*.stps', 'Touched — PLC log steps inserted per the rules above.'],
    ['CPMoudle/**', 'Skipped — different XML schema, never modified.'],
    ['Old_CamSteps3/**', 'Skipped — in-backup history, never modified.'],
    ['ProSteps_0001..1000', 'Skipped — system / standard step templates.'],
    ['Everything else', 'Read-only. SHA-256 checksum verified pre- and post-run; any drift fails the build.'],
];

export const TubeProPLCDebuggerPage: React.FC = () => {
    return (
        <div style={s.page}>
            <div style={s.container}>
                {/* Top bar with home link */}
                <div style={s.topBar}>
                    <a href="/" style={s.backLink}>← XOVR/tools</a>
                </div>

                {/* Hero */}
                <header style={s.hero}>
                    <div style={s.eyebrow}>For TubePro / CypCut service engineers</div>
                    <h1 style={s.title}>TubePro PLC Debugger</h1>
                    <p style={s.lede}>
                        Load a customer&rsquo;s <code style={s.code}>.cfgpkg</code> backup, inject
                        debug Log steps at every program entry, every If branch, every call
                        out to another PLC or script, and every program exit, and produce
                        an otherwise-identical backup. Load it on the machine to follow
                        the exact path the PLC took when an operator complaint shows up.
                    </p>

                    <div style={s.ctaRow}>
                        <a href={EXE_HREF} download style={s.primaryBtn}>
                            <span style={{ marginRight: 8 }}>↓</span>
                            Download for Windows ({EXE_SIZE_MB} MB)
                        </a>
                    </div>

                    <div style={s.metaRow}>
                        v{APP_VERSION} &middot; Windows 10 / 11 &middot; Standalone EXE,
                        no installer required &middot; Output is a drop-in replacement
                        <code style={s.code}>.cfgpkg</code>
                    </div>
                </header>

                {/* Features */}
                <Section title="What it does">
                    <div style={s.grid4}>
                        {FEATURES.map((f) => (
                            <ValueCard key={f.head} head={f.head} body={f.body} />
                        ))}
                    </div>
                </Section>

                {/* Workflow */}
                <Section title="How a debug session goes" subtitle="Same backup the machine already loads, debug-annotated, loaded back the same way.">
                    <div style={s.grid3}>
                        <Step n={1} head="Open the backup">
                            Browse to the customer&rsquo;s <code style={s.code}>.cfgpkg</code>.
                            The app detects whether it is plain or AES-encrypted and extracts to a temp
                            working folder. The original is never modified.
                        </Step>
                        <Step n={2} head="Process &amp; review">
                            Click <em>Process</em>. The injector walks every PLC program, inserts log
                            steps per the rules, and re-serializes the XML. Hit <em>View Report</em> for
                            the full per-file summary or <em>View Diffs</em> for unified diffs.
                        </Step>
                        <Step n={3} head="Save &amp; deploy">
                            Click <em>Save Output</em>. The app repacks the working folder back into a
                            <code style={s.code}>.cfgpkg</code> using the same encryption type as the input
                            and writes the verification report and diffs alongside it. Load on the machine
                            the same way you load any backup.
                        </Step>
                    </div>
                </Section>

                {/* Injection rules */}
                <Section title="Where the logs go" subtitle="Four placement rules plus one no-touch rule. Deterministic. Documented.">
                    <div style={s.tableWrap}>
                        <table style={s.table}>
                            <thead>
                                <tr>
                                    <th style={s.th}>Location</th>
                                    <th style={s.th}>Inserted log</th>
                                </tr>
                            </thead>
                            <tbody>
                                {RULES.map(([loc, rule]) => (
                                    <tr key={loc}>
                                        <td style={{ ...s.td, color: '#fff', fontWeight: 600, whiteSpace: 'nowrap' }}>{loc}</td>
                                        <td style={s.td}>{rule}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Section>

                {/* Scope */}
                <Section title="What it touches (and what it never touches)">
                    <div style={s.grid2}>
                        <div>
                            <p style={s.body}>
                                The injector has a hardcoded scope. PLC step files inside
                                <code style={s.code}>data/CamSteps3</code> get log steps. Everything
                                else in the backup is read-only &mdash; and a SHA-256 checksum sweep
                                before and after the run verifies that promise.
                            </p>
                            <ul style={s.bullets}>
                                <li>Original <code style={s.code}>.cfgpkg</code> is never modified.</li>
                                <li>Numeric values, ports, timings, coordinates: never changed.</li>
                                <li>Output XML is round-tripped through the parser before write &mdash; if it does not re-parse, the file is rejected and the issue is in the report.</li>
                                <li>Encryption type (plain or AES-encrypted) is preserved when repacking.</li>
                            </ul>
                        </div>
                        <div style={s.tableWrap}>
                            <table style={s.table}>
                                <thead>
                                    <tr>
                                        <th style={s.th}>Path</th>
                                        <th style={s.th}>Treatment</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {SCOPE.map(([path, treat]) => (
                                        <tr key={path}>
                                            <td style={{ ...s.td, fontFamily: 'JetBrains Mono, ui-monospace, monospace', whiteSpace: 'nowrap' }}>{path}</td>
                                            <td style={s.td}>{treat}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </Section>

                {/* FAQ */}
                <Section title="FAQ">
                    <div style={s.grid2}>
                        <Faq q="Will this break the machine?" a="No. The output is a structurally identical .cfgpkg with extra Log steps inside PLC programs. PLC Log is a standard step type that TubePro and CypCut both already render and execute as a no-op (log to console). Numbers, ports, timings, and coordinates are bit-for-bit unchanged." />
                        <Faq q="What about machines that don't run PLC Log?" a="Older firmware that does not support a step type ignores it rather than faulting. If you are on an unknown firmware, run on a non-production machine first — but the tool is in active use on TubePro and CypCut HMI revisions in the field." />
                        <Faq q="How do I see the logs once the backup is loaded?" a="The PLC Log step writes to the HMI's PLC console (TubePro: PLC Management → Output / Log panel). Filter on the PLC{N}: prefix to follow a single program." />
                        <Faq q="Can I re-run on an already debug-annotated backup?" a="Yes — it is idempotent. Existing PLC Logs are preserved verbatim. The second run produces a byte-identical output and the report shows zero new logs added." />
                        <Faq q="Do my customer's files leave the workshop PC?" a="No. Everything is local. The app extracts to a temp folder, edits in place, and repacks. There is no network call." />
                        <Faq q="Why does Windows SmartScreen warn me?" a="The build is unsigned. Click 'More info' → 'Run anyway.' Code-signing will come once usage settles." />
                    </div>
                </Section>

                <footer style={s.footer}>
                    XOVR Parts &amp; Service &mdash; TubePro PLC Debugger v{APP_VERSION} &middot;
                    For TubePro / CypCut fiber laser tube cutters.
                </footer>
            </div>
        </div>
    );
};

/* ─────────────────── Sub-components ─────────────────── */

const Section: React.FC<{ title: string; subtitle?: string; children: React.ReactNode }> = ({ title, subtitle, children }) => (
    <section style={s.section}>
        <h2 style={s.h2}>{title}</h2>
        {subtitle && <p style={s.subtitle}>{subtitle}</p>}
        <div style={{ marginTop: 24 }}>{children}</div>
    </section>
);

const ValueCard: React.FC<{ head: string; body: string }> = ({ head, body }) => (
    <div style={s.card}>
        <div style={s.accentBar} />
        <div style={s.cardHead}>{head}</div>
        <div style={s.cardBody}>{body}</div>
    </div>
);

const Step: React.FC<{ n: number; head: string; children: React.ReactNode }> = ({ n, head, children }) => (
    <div style={s.card}>
        <div style={s.stepRow}>
            <span style={s.stepBubble}>{n}</span>
            <span style={s.cardHead}>{head}</span>
        </div>
        <div style={{ ...s.cardBody, marginTop: 12 }}>{children}</div>
    </div>
);

const Faq: React.FC<{ q: string; a: string }> = ({ q, a }) => (
    <div style={s.card}>
        <div style={s.cardHead}>{q}</div>
        <div style={s.cardBody}>{a}</div>
    </div>
);

/* ─────────────────────── Styles ───────────────────────── */
const s: Record<string, React.CSSProperties> = {
    page: {
        minHeight: '100vh',
        background: 'radial-gradient(ellipse at top, #1a1a1a 0%, #0a0a0a 100%)',
        color: '#e5e5e5',
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        padding: '40px 20px 80px',
    },
    container: { maxWidth: 1080, margin: '0 auto' },
    topBar: { marginBottom: 24 },
    backLink: { color: '#888', textDecoration: 'none', fontSize: 13 },

    /* Hero */
    hero: {
        padding: '32px 0 56px',
        borderBottom: '1px solid #1f1f1f',
        textAlign: 'left',
    },
    eyebrow: {
        display: 'inline-block',
        padding: '4px 10px',
        borderRadius: 999,
        border: '1px solid #2a2a2a',
        background: '#141414',
        color: '#bbb',
        fontSize: 12,
        marginBottom: 16,
    },
    title: {
        fontSize: 44,
        lineHeight: 1.1,
        fontWeight: 800,
        letterSpacing: '-0.02em',
        color: '#ffffff',
        margin: 0,
    },
    lede: {
        marginTop: 16,
        color: '#bbb',
        fontSize: 17,
        lineHeight: 1.55,
        maxWidth: 760,
    },
    ctaRow: { marginTop: 28, display: 'flex', flexWrap: 'wrap', gap: 12 },
    primaryBtn: {
        display: 'inline-flex',
        alignItems: 'center',
        background: '#ff4500',
        color: '#fff',
        textDecoration: 'none',
        padding: '12px 18px',
        borderRadius: 8,
        fontWeight: 600,
        fontSize: 14,
        boxShadow: '0 6px 16px rgba(255, 69, 0, 0.25)',
    },
    metaRow: { marginTop: 12, color: '#777', fontSize: 12, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6 },

    /* Sections */
    section: { padding: '56px 0', borderBottom: '1px solid #1f1f1f' },
    h2: { fontSize: 26, fontWeight: 700, letterSpacing: '-0.01em', color: '#fff', margin: 0 },
    subtitle: { marginTop: 8, color: '#999', fontSize: 15, maxWidth: 720 },
    body: { color: '#bbb', fontSize: 15, lineHeight: 1.6, margin: 0 },

    /* Grids */
    grid4: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: 16,
    },
    grid3: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: 20,
    },
    grid2: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
        gap: 20,
    },

    /* Cards */
    card: {
        background: '#141414',
        border: '1px solid #2a2a2a',
        borderRadius: 12,
        padding: 20,
    },
    accentBar: {
        width: 28,
        height: 3,
        borderRadius: 999,
        background: '#ff4500',
        marginBottom: 12,
    },
    cardHead: { color: '#fff', fontWeight: 600, fontSize: 15 },
    cardBody: { color: '#999', fontSize: 13.5, lineHeight: 1.55, marginTop: 8 },

    /* Step */
    stepRow: { display: 'flex', alignItems: 'center', gap: 10 },
    stepBubble: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 26,
        height: 26,
        borderRadius: 999,
        background: '#ff4500',
        color: '#fff',
        fontSize: 13,
        fontWeight: 700,
    },

    /* Bullets */
    bullets: {
        marginTop: 14,
        paddingLeft: 18,
        color: '#bbb',
        fontSize: 14,
        lineHeight: 1.6,
    },

    /* Table */
    tableWrap: {
        background: '#141414',
        border: '1px solid #2a2a2a',
        borderRadius: 12,
        overflow: 'hidden',
    },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: 14 },
    th: {
        textAlign: 'left',
        padding: '12px 16px',
        background: '#0f0f0f',
        color: '#888',
        fontSize: 11,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        borderBottom: '1px solid #2a2a2a',
        fontWeight: 600,
    },
    td: { padding: '12px 16px', color: '#ccc', borderBottom: '1px solid #1f1f1f' },

    /* Misc */
    code: {
        background: '#0f0f0f',
        border: '1px solid #2a2a2a',
        borderRadius: 4,
        padding: '1px 6px',
        fontFamily: 'JetBrains Mono, ui-monospace, monospace',
        fontSize: 13,
        color: '#e5e5e5',
        margin: '0 2px',
    },
    footer: {
        marginTop: 56,
        paddingTop: 24,
        textAlign: 'center',
        color: '#555',
        fontSize: 12,
        borderTop: '1px solid #1f1f1f',
    },
};

export default TubeProPLCDebuggerPage;
